import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { filter, map, pairwise } from 'rxjs/operators';
import { SecurityManagementService } from './features/security-management/security-management.service';
import { SwUpdate } from '@angular/service-worker';
import { ToastrService } from 'ngx-toastr';
import { ENVIRONMENT } from './tokens';
import { LaunchDarklyService } from './core/layout/launch-darkly/launch-darkly.service';
import { ELaunchDarklyFlag } from './core/constants/pol.enums';
import { select, Store } from '@ngrx/store';
import { IUser } from './core/models/state.model';
import { getSignedInUser } from './core/state/core.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Provider Online';

  private _destroyRef = inject(DestroyRef);
  private _store = inject(Store);
  toastr = inject(ToastrService);
  environment = inject(ENVIRONMENT);
  launchDarkly = inject(LaunchDarklyService);
  protected readonly showChat = signal(false);
  private readonly forceUpdate_ = signal(false);

  constructor(
    private router: Router,
    private securityManageService: SecurityManagementService,
    private swUpdate: SwUpdate
  ) {
    this.router.events
      .pipe(filter((evt: any) => evt instanceof RoutesRecognized), pairwise())
      .subscribe((events: RoutesRecognized[]) => {
        // use router.event to track the pages' url and use it in any module:
        this.securityManageService.previousUrl = events[0].urlAfterRedirects;
      });
  }

  ngOnInit(): void {
    const environment = window.ENVIRONMENT;
    const user$ = this._store.pipe(select(getSignedInUser));
    const flags$ = this.launchDarkly.flags$;
    const isLocalhost = location.host.includes('localhost');

    // Don't load app dynamics in localhost.
    // App dynamics will create it's own stack trace for exceptions,
    // which makes debugging much more difficult
    if (!isLocalhost) {
      this.addAppDynamicsScript(environment);
      this.addAppDynamicsExternalScript();
      this.addEumUsernameScript();
      this.addPiwikScript();
    }

    this.loadIQCScript();

    // check for app version updates from the service worker
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(event => {
        if (event.type === 'VERSION_READY') {
          if (this.forceUpdate_()) {
            location.reload();
          } else {
            this.toastr.info(
              'New version is available. Save work then refresh page to update.',
              'App update',
              {
                closeButton: true,
                disableTimeOut: true,
                positionClass: 'toast-top-center',
                tapToDismiss: false
              }
            );
          }
        }
      });
    }

    // We need to ensure that the user profile is available before displaying the chat options
    // in order to determine the correct set of chat topics for the user based on provider type
    this._isChatShown(user$, flags$).subscribe(res => this.showChat.set(res));

    // flag to force app updates. this is helpful if we have major infrastructure
    // changes (like East -> East2) and we need to force every user to update
    this.launchDarkly.flags$.subscribe(res => {
      this.forceUpdate_.set(res[ELaunchDarklyFlag.KillSwitchForceAppUpdate]);
    });

    // get live updates for the force update flag
    this.launchDarkly.watchForChanges<boolean>(ELaunchDarklyFlag.KillSwitchForceAppUpdate);
  }

  ngOnDestroy(): void {
    this.launchDarkly.shutDown().then();
  }

  private _isChatShown(user$: Observable<IUser>, flags$: BehaviorSubject<Record<string, boolean>>): Observable<boolean> {

    return combineLatest([user$, flags$]).pipe(
      map(([user, flags]) => {
        const showChat = !!user.providerType && flags[ELaunchDarklyFlag.ShowHomepageChat];
        return showChat;
      }),
      takeUntilDestroyed(this._destroyRef)
    );
  }

  /**
   * Injects embedded AppDynamics script
   * @param {Object} environment - environment config object
   */
  addAppDynamicsScript(environment: any): void {
    const { appDynamics, env } = environment;
    const appDynamicsScript = document.createElement('script');
    appDynamicsScript.charset = 'UTF-8';
    appDynamicsScript.innerHTML = `
      window["adrum-start-time"] = new Date().getTime();
      (function(config){
        config.appKey = "${appDynamics.appKey}";
        config.adrumExtUrlHttp = "http://cdn.appdynamics.com";
        config.adrumExtUrlHttps = "https://cdn.appdynamics.com";
        config.beaconUrlHttp = "http://${appDynamics?.beaconUrl}";
        config.beaconUrlHttps = "https://${appDynamics?.beaconUrl}";
        config.resTiming = {"bufSize":200,"clearResTimingOnBeaconSend":true};
        config.maxUrlLength = 512;
        config.spa = {"spa2":true};
        ${env === 'prod' ? `config.xd = {enable : false}` : ``}
      })(window["adrum-config"] || (window["adrum-config"] = {}));
    `;
    document.head.appendChild(appDynamicsScript);
  }

  /**
   * Injects external AppDynamics script
   */
  addAppDynamicsExternalScript(): void {
    const externalAppDynamicsScript = document.createElement('script');
    externalAppDynamicsScript.src = `//cdn.appdynamics.com/adrum/adrum-latest.js`;
    document.head.appendChild(externalAppDynamicsScript);
  }

  addPiwikScript(): void {
    const envObj = this.environment as any;
    const siteId = envObj.env.toLowerCase() === 'prod' ? '2abc2f17-8242-4069-a14d-37566307bfd2' : 'e7c9e2ba-d9bc-46fc-ba98-58a6b1ad613e';
    const script = `
      (function(window, document, dataLayerName, id) {
      window[dataLayerName]=window[dataLayerName]||[],window[dataLayerName].push({start:(new Date).getTime(),event:"stg.start"});var scripts=document.getElementsByTagName('script')[0],tags=document.createElement('script');
      function stgCreateCookie(a,b,c){var d="";if(c){var e=new Date;e.setTime(e.getTime()+24*c*60*60*1e3),d="; expires="+e.toUTCString();f="; SameSite=Strict"}document.cookie=a+"="+b+d+f+"; path=/"}
      var isStgDebug=(window.location.href.match("stg_debug")||document.cookie.match("stg_debug"))&&!window.location.href.match("stg_disable_debug");stgCreateCookie("stg_debug",isStgDebug?1:"",isStgDebug?14:-1);
      var qP=[];dataLayerName!=="dataLayer"&&qP.push("data_layer_name="+dataLayerName),isStgDebug&&qP.push("stg_debug");var qPString=qP.length>0?("?"+qP.join("&")):"";
      tags.async=!0,tags.src="https://upmc.containers.piwik.pro/"+id+".js"+qPString,scripts.parentNode.insertBefore(tags,scripts);
      !function(a,n,i){a[n]=a[n]||{};for(var c=0;c<i.length;c++)!function(i){a[n][i]=a[n][i]||{},a[n][i].api=a[n][i].api||function(){var a=[].slice.call(arguments,0);"string"==typeof a[0]&&window[dataLayerName].push({event:n+"."+i+":"+a[0],parameters:[].slice.call(arguments,1)})}}(i[c])}(window,"ppms",["tm","cm"]);
      })(window, document, 'dataLayer', '${siteId}');
    `;
    const scriptEl = document.createElement('script');
    scriptEl.innerHTML = script;
    document.head.appendChild(scriptEl);
  }

  addEumUsernameScript(): void {
    const eumScript = document.createElement('script');
    eumScript.innerHTML = `
      let timeoutId = setInterval(() => {
        for(let i = 0; i < sessionStorage.length; i++) {
          if(ADRUM && (sessionStorage.key(i).match(/po-b2c.user:.*952eb08f-8a2b-453e-b313-f6165cd8fe84/gi))) {
            try {
              const username = JSON.parse(sessionStorage.getItem(sessionStorage.key(i))).profile.preferred_username;
              ADRUM.command("addUserData", "username", username);
            } finally {
              clearInterval(timeoutId);
              break;
            }
          }
        }
      }, 10);
    `;

    document.head.appendChild(eumScript);
  }

  loadIQCScript() {
    const envObj = this.environment as any;
    const IQCUrl =
      envObj.env.toLowerCase() === "prod"
        ? "https://prod.ds.interqual.com/caas/js/ap/interqual-api-2.2.js"
        : "https://integ.ds.interqual.com/caas/js/ap/interqual-api-2.2.js";

    const script = document.createElement("script");
    script.src = IQCUrl;
    script.type = "text/javascript";
    script.async = true;
    script.onload = (error) => {
      console.error(`Error while loading script ${IQCUrl}`, error);
    };
    document.body.appendChild(script);
  }
}
